import type { NegotiationResponseType } from './types';

/**
 * [MCV-1770] Client mirror of the server formula that fills
 * total_contract_value and avg_hourly_rate when a negotiation version is
 * created (libs/models/models/mixins/job_listing_mixin: MSaaSMixin /
 * JobListingMixin.calculate_and_update_derived_fields, base_mixin's shift
 * differential, calculations.py). Keep the two in step: the proposed column
 * must show what the server would store if the offer were submitted now.
 *
 * Python's Decimal with ROUND_HALF_UP cannot be reproduced with floats
 * (33.33 * 7.5 is 249.97499999999997 in binary, which rounds to 249.97 where
 * the server stores 249.98), so every value here is an exact fraction.
 */

type Fraction = { n: bigint; d: bigint };
type Terms = Record<string, unknown>;

export type ProposedContractValue = {
  totalContractValue: string;
  avgHourlyRate: string;
};

// BigInt() calls rather than literals: the app compiles with target es2015.
const ZERO_N = BigInt(0);
const ONE_N = BigInt(1);
const TWO_N = BigInt(2);
const HUNDRED_N = BigInt(100);

const ZERO: Fraction = { n: ZERO_N, d: ONE_N };

const whole = (value: number): Fraction => ({ n: BigInt(value), d: ONE_N });
const add = (a: Fraction, b: Fraction): Fraction => ({ n: a.n * b.d + b.n * a.d, d: a.d * b.d });
const subtract = (a: Fraction, b: Fraction): Fraction => ({
  n: a.n * b.d - b.n * a.d,
  d: a.d * b.d,
});
const multiply = (...values: Fraction[]): Fraction =>
  values.reduce((a, b) => ({ n: a.n * b.n, d: a.d * b.d }), { n: ONE_N, d: ONE_N });
// Callers only divide by values already checked to be positive.
const divide = (a: Fraction, b: Fraction): Fraction => ({ n: a.n * b.d, d: a.d * b.n });
const isPositive = (value: Fraction | undefined): value is Fraction =>
  value !== undefined && value.n > ZERO_N;
const max = (a: Fraction, b: Fraction): Fraction => (subtract(a, b).n >= ZERO_N ? a : b);

// Python's quantize(..., ROUND_HALF_UP): halves round away from zero.
const roundHalfUp = (value: Fraction, scale: bigint): Fraction => {
  const negative = value.n < ZERO_N;
  const magnitude = negative ? -value.n : value.n;
  const rounded = (magnitude * scale * TWO_N + value.d) / (TWO_N * value.d);
  return { n: negative ? -rounded : rounded, d: scale };
};
const roundCurrency = (value: Fraction): Fraction => roundHalfUp(value, HUNDRED_N);
const roundWhole = (value: Fraction): Fraction => roundHalfUp(value, ONE_N);
const ceilWhole = (value: Fraction): Fraction => ({
  n: (value.n + value.d - ONE_N) / value.d,
  d: ONE_N,
});

const toMoneyString = (value: Fraction): string => {
  const cents = roundCurrency(value).n;
  const negative = cents < ZERO_N;
  const magnitude = (negative ? -cents : cents).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${magnitude.slice(0, -2)}.${magnitude.slice(-2)}`;
};

/** The server cannot price these terms, so it stores no new total. */
class NotCalculable extends Error {}

const DECIMAL_PATTERN = /^(\d+(\.\d+)?|\.\d+)$/;
const WHOLE_PATTERN = /^\d+$/;

/**
 * Exact value of a form/API number. Absent (null/undefined) is `undefined`
 * so callers can apply the server's defaults; anything else that is not a
 * plain non-negative number (a cleared input, NaN, a negative) is not
 * calculable.
 */
const readNumber = (value: unknown, pattern: RegExp): Fraction | undefined => {
  if (value === null || value === undefined) return undefined;
  const text =
    typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  if (!pattern.test(text)) throw new NotCalculable();
  const [integerDigits, fractionDigits = ''] = text.split('.');
  return {
    n: BigInt(integerDigits + fractionDigits),
    d: BigInt(`1${'0'.repeat(fractionDigits.length)}`),
  };
};
const readDecimal = (value: unknown) => readNumber(value, DECIMAL_PATTERN);
const readWhole = (value: unknown) => readNumber(value, WHOLE_PATTERN);

const readOption = (value: unknown): unknown =>
  value !== null && typeof value === 'object' ? (value as { value?: unknown }).value : value;

const SHIFT_BUCKETS = [
  ['numDayShifts', undefined],
  ['numEveShifts', 'eveShiftRateDiff'],
  ['numNightShifts', 'nightShiftRateDiff'],
  ['numOfWeDayShifts', 'weDayShiftRateDiff'],
  ['numOfWeEveShifts', 'weEveShiftRateDiff'],
  ['numOfWeNightShifts', 'weNightShiftRateDiff'],
  ['numHolDayShifts', 'holDayShiftRateDiff'],
  ['numHolEveShifts', 'holEveShiftRateDiff'],
  ['numHolNightShifts', 'holNightShiftRateDiff'],
] as const;

const CALL_BUCKETS = [
  ['numDayCallShifts', undefined],
  ['numEveCallShifts', 'eveCallShiftRateDiff'],
  ['numNightCallShifts', 'nightCallShiftRateDiff'],
  ['numOfWeDayCallShifts', 'weDayCallShiftRateDiff'],
  ['numOfWeEveCallShifts', 'weEveCallShiftRateDiff'],
  ['numOfWeNightCallShifts', 'weNightCallShiftRateDiff'],
  ['numHolDayCallShifts', 'holDayCallShiftRateDiff'],
  ['numHolEveCallShifts', 'holEveCallShiftRateDiff'],
  ['numHolNightCallShifts', 'holNightCallShiftRateDiff'],
] as const;

type DifferentialContext = {
  terms: Terms;
  pricePerHour: Fraction;
  hoursPerShift: Fraction;
  pricePerRvu: Fraction | undefined;
  rvusPerShift: Fraction | undefined;
};

/** base_mixin.calculate_total_shift_differential. */
const calculateShiftDifferential = (
  { terms, pricePerHour, hoursPerShift, pricePerRvu, rvusPerShift }: DifferentialContext,
  shiftDiffType: unknown
): Fraction => {
  const baseShiftRate = multiply(pricePerHour, hoursPerShift);
  let allocated = ZERO;
  let total = ZERO;
  for (const [countKey, diffKey] of SHIFT_BUCKETS) {
    const count = readWhole(terms[countKey]) ?? ZERO;
    if (!isPositive(count)) continue;
    allocated = add(allocated, count);
    const differential = diffKey ? readDecimal(terms[diffKey]) : undefined;
    let compensation: Fraction;
    if (!isPositive(differential) || !shiftDiffType) {
      compensation = roundCurrency(multiply(baseShiftRate, count));
    } else if (shiftDiffType === 'RVU') {
      compensation = roundCurrency(
        rvusPerShift ? multiply(differential, rvusPerShift, count) : multiply(baseShiftRate, count)
      );
    } else if (shiftDiffType === 'INCR_HOURLY_RATE') {
      compensation = roundCurrency(
        multiply(add(baseShiftRate, multiply(differential, hoursPerShift)), count)
      );
    } else if (shiftDiffType === 'MULTIPLIER') {
      compensation = roundCurrency(multiply(baseShiftRate, differential, count));
    } else if (shiftDiffType === 'HOURLY_RATE') {
      compensation = roundCurrency(multiply(differential, hoursPerShift, count));
    } else {
      throw new NotCalculable();
    }
    total = add(total, compensation);
  }
  if (!isPositive(allocated)) return ZERO;
  const baseForAllocated = roundCurrency(
    shiftDiffType === 'RVU' && rvusPerShift && isPositive(pricePerRvu)
      ? multiply(pricePerRvu, rvusPerShift, allocated)
      : multiply(baseShiftRate, allocated)
  );
  return max(subtract(total, baseForAllocated), ZERO);
};

/** JobListingMixin.calculate_total_call_shift_differential. */
const calculateCallDifferential = (
  { terms, hoursPerShift, pricePerRvu, rvusPerShift }: DifferentialContext,
  pricePerCallShift: Fraction,
  callDiffType: unknown
): Fraction => {
  if (!isPositive(pricePerCallShift)) return ZERO;
  let allocated = ZERO;
  let total = ZERO;
  for (const [countKey, diffKey] of CALL_BUCKETS) {
    const count = readWhole(terms[countKey]) ?? ZERO;
    if (!isPositive(count)) continue;
    allocated = add(allocated, count);
    const differential = diffKey ? readDecimal(terms[diffKey]) : undefined;
    let compensation: Fraction;
    if (!isPositive(differential) || !callDiffType) {
      compensation = roundCurrency(multiply(pricePerCallShift, count));
    } else if (callDiffType === 'RVU') {
      compensation = roundCurrency(
        rvusPerShift
          ? multiply(differential, rvusPerShift, count)
          : multiply(pricePerCallShift, count)
      );
    } else if (callDiffType === 'INCR_HOURLY_RATE') {
      compensation = roundCurrency(
        multiply(add(pricePerCallShift, multiply(differential, hoursPerShift)), count)
      );
    } else if (callDiffType === 'MULTIPLIER') {
      compensation = roundCurrency(multiply(pricePerCallShift, differential, count));
    } else if (callDiffType === 'HOURLY_RATE') {
      compensation = roundCurrency(multiply(differential, hoursPerShift, count));
    } else {
      throw new NotCalculable();
    }
    total = add(total, compensation);
  }
  if (!isPositive(allocated)) return ZERO;
  const baseForAllocated = roundCurrency(
    callDiffType === 'RVU' && rvusPerShift && isPositive(pricePerRvu)
      ? multiply(pricePerRvu, rvusPerShift, allocated)
      : multiply(pricePerCallShift, allocated)
  );
  return max(subtract(total, baseForAllocated), ZERO);
};

const requirePositive = (value: Fraction | undefined): Fraction => {
  if (!isPositive(value)) throw new NotCalculable();
  return value;
};

const toResult = (total: Fraction, totalHours: Fraction): ProposedContractValue => ({
  totalContractValue: toMoneyString(total),
  avgHourlyRate: toMoneyString(divide(total, totalHours)),
});

/** MSaaSMixin.calculate_and_update_derived_fields. */
const calculateMsaas = (terms: Terms): ProposedContractValue => {
  const fteShiftsPerYear = requirePositive(readWhole(terms.fteShiftsPerYear) ?? whole(200));
  const hoursPerShift = requirePositive(readDecimal(terms.hoursPerShift) ?? whole(8));
  const minRvusPerShift = readWhole(terms.minRvusPerShift);
  let numOfFtes: Fraction | undefined;
  // The submit transforms send `value || null` for the three prices and MSaaS
  // yearlyRvusPerFte, so 0 and '' reach the server as null.
  let yearlyRvusPerFte = readWhole(terms.yearlyRvusPerFte || null);

  if (terms.laborReqType === 'RVU') {
    // _derive_fte_from_rvu_requirements: the form's FTEs and yearly RVUs are ignored.
    const numRvusReq = requirePositive(readWhole(terms.numRvusReq));
    const requiredShifts = ceilWhole(divide(numRvusReq, requirePositive(minRvusPerShift)));
    numOfFtes = ceilWhole(roundCurrency(divide(requiredShifts, fteShiftsPerYear)));
    yearlyRvusPerFte = roundWhole(divide(numRvusReq, requirePositive(numOfFtes)));
  } else {
    numOfFtes = readWhole(terms.numOfFtes);
    // _derive_yearly_rvus_for_fte_mode overrides the form's yearly RVUs.
    if (isPositive(minRvusPerShift)) yearlyRvusPerFte = multiply(minRvusPerShift, fteShiftsPerYear);
  }
  const ftes = requirePositive(numOfFtes);

  // validate_required_fields / validate_positive_values reject any price or
  // yearly RVU figure that is sent but not positive.
  const coercedPrices = {
    HOUR: readDecimal(terms.pricePerHour || null),
    SHIFT: readDecimal(terms.pricePerShift || null),
    RVU: readDecimal(terms.pricePerRvu || null),
  };
  for (const value of [...Object.values(coercedPrices), yearlyRvusPerFte]) {
    if (value !== undefined) requirePositive(value);
  }

  const compType = readOption(terms.compType);
  let pricePerHour: Fraction;
  let pricePerRvu: Fraction | undefined;
  if (compType === 'HOUR') {
    pricePerHour = requirePositive(coercedPrices.HOUR);
    pricePerRvu = isPositive(yearlyRvusPerFte)
      ? roundCurrency(
          divide(multiply(pricePerHour, hoursPerShift, fteShiftsPerYear), yearlyRvusPerFte)
        )
      : undefined;
  } else if (compType === 'SHIFT') {
    const pricePerShift = requirePositive(coercedPrices.SHIFT);
    pricePerHour = roundCurrency(divide(pricePerShift, hoursPerShift));
    pricePerRvu = isPositive(yearlyRvusPerFte)
      ? roundCurrency(divide(multiply(pricePerShift, fteShiftsPerYear), yearlyRvusPerFte))
      : undefined;
  } else if (compType === 'RVU') {
    pricePerRvu = requirePositive(coercedPrices.RVU);
    const yearlyRvus = requirePositive(yearlyRvusPerFte);
    pricePerHour = roundCurrency(
      divide(multiply(pricePerRvu, yearlyRvus), multiply(fteShiftsPerYear, hoursPerShift))
    );
  } else {
    throw new NotCalculable();
  }

  const shiftDiffType = readOption(terms.shiftDiffType);
  let rvusPerShift: Fraction | undefined;
  if (shiftDiffType === 'RVU') {
    // calculations.normalize_rvus_per_shift (MSaaS arguments)
    if (isPositive(minRvusPerShift)) rvusPerShift = minRvusPerShift;
    else if (isPositive(yearlyRvusPerFte))
      rvusPerShift = divide(yearlyRvusPerFte, fteShiftsPerYear);
  }

  const totalHours = requirePositive(roundWhole(multiply(ftes, fteShiftsPerYear, hoursPerShift)));
  requirePositive(pricePerHour);
  const baseContractValue = roundCurrency(multiply(pricePerHour, totalHours));
  const shiftDifferential = calculateShiftDifferential(
    { terms, pricePerHour, hoursPerShift, pricePerRvu, rvusPerShift },
    shiftDiffType
  );
  return toResult(add(baseContractValue, shiftDifferential), totalHours);
};

/** JobListingMixin.calculate_and_update_derived_fields. */
const calculateJob = (terms: Terms): ProposedContractValue => {
  let numOfShifts = requirePositive(readWhole(terms.numOfShifts) ?? whole(200));
  const hoursPerShift = requirePositive(readDecimal(terms.hoursPerShift) ?? whole(8));
  const minRvusPerShift = readWhole(terms.minRvusPerShift);
  let rvusPerPeriod = readWhole(terms.rvusPerPeriod);
  let rvuPeriod = terms.rvuPeriod;

  if (terms.laborReqType === 'RVU') {
    // _derive_shifts_from_rvu_requirements: the form's shift count is ignored.
    const numRvusReq = requirePositive(readWhole(terms.numRvusReq));
    numOfShifts = ceilWhole(divide(numRvusReq, requirePositive(minRvusPerShift)));
    rvuPeriod = 'SHIFT';
    rvusPerPeriod = minRvusPerShift;
  }

  const compType = readOption(terms.compType);
  let pricePerHour: Fraction;
  let pricePerShift: Fraction;
  let pricePerRvu: Fraction | undefined;
  if (compType === 'HOUR') {
    pricePerHour = requirePositive(readDecimal(terms.pricePerHour || null));
    pricePerShift = roundCurrency(multiply(pricePerHour, hoursPerShift));
    if (isPositive(rvusPerPeriod)) {
      const periodPrice =
        rvuPeriod === 'SHIFT'
          ? multiply(pricePerHour, hoursPerShift)
          : rvuPeriod === 'HOUR'
            ? pricePerHour
            : multiply(pricePerHour, hoursPerShift, numOfShifts);
      pricePerRvu = roundCurrency(divide(periodPrice, rvusPerPeriod));
    }
  } else if (compType === 'SHIFT') {
    pricePerShift = requirePositive(readDecimal(terms.pricePerShift || null));
    pricePerHour = roundCurrency(divide(pricePerShift, hoursPerShift));
    if (isPositive(rvusPerPeriod)) {
      const periodPrice =
        rvuPeriod === 'SHIFT'
          ? pricePerShift
          : rvuPeriod === 'HOUR'
            ? divide(pricePerShift, hoursPerShift)
            : multiply(pricePerShift, numOfShifts);
      pricePerRvu = roundCurrency(divide(periodPrice, rvusPerPeriod));
    }
  } else if (compType === 'RVU') {
    pricePerRvu = requirePositive(readDecimal(terms.pricePerRvu || null));
    // Without RVUs per period the server cannot derive the rates and keeps
    // the previous version's totals instead of pricing this offer.
    const rvus = requirePositive(rvusPerPeriod);
    const periodPrice = multiply(pricePerRvu, rvus);
    let rawPricePerShift: Fraction;
    let rawPricePerHour: Fraction;
    if (rvuPeriod === 'SHIFT') {
      rawPricePerShift = periodPrice;
      rawPricePerHour = divide(periodPrice, hoursPerShift);
    } else if (rvuPeriod === 'HOUR') {
      rawPricePerHour = periodPrice;
      rawPricePerShift = multiply(periodPrice, hoursPerShift);
    } else {
      rawPricePerShift = divide(periodPrice, numOfShifts);
      rawPricePerHour = divide(rawPricePerShift, hoursPerShift);
    }
    pricePerShift = roundCurrency(rawPricePerShift);
    pricePerHour = roundCurrency(rawPricePerHour);
  } else {
    throw new NotCalculable();
  }

  // An unset call-shift base price takes this version's price per shift; a
  // carried-over one is kept as-is.
  const pricePerCallShift = readDecimal(terms.pricePerCallShift) ?? pricePerShift;

  const shiftDiffType = readOption(terms.shiftDiffType);
  const callDiffType = terms.callShiftDiffType;
  let rvusPerShift: Fraction | undefined;
  if (shiftDiffType === 'RVU' || callDiffType === 'RVU') {
    // calculations.normalize_rvus_per_shift (job listing arguments)
    if (isPositive(minRvusPerShift)) rvusPerShift = minRvusPerShift;
    else if (isPositive(rvusPerPeriod)) {
      rvusPerShift =
        rvuPeriod === 'SHIFT'
          ? rvusPerPeriod
          : rvuPeriod === 'HOUR'
            ? multiply(rvusPerPeriod, hoursPerShift)
            : divide(rvusPerPeriod, numOfShifts);
    }
  }

  requirePositive(pricePerHour);
  const totalHours = requirePositive(roundWhole(multiply(numOfShifts, hoursPerShift)));
  const baseContractValue = roundCurrency(multiply(pricePerHour, totalHours));
  const context = { terms, pricePerHour, hoursPerShift, pricePerRvu, rvusPerShift };
  const total = add(
    add(baseContractValue, calculateShiftDifferential(context, shiftDiffType)),
    calculateCallDifferential(context, pricePerCallShift, callDiffType)
  );
  return toResult(total, totalHours);
};

/**
 * The total contract value and average hourly rate the server would store
 * for `terms` (the baseline merged with the current form values, exactly as
 * the submit/counter handlers send them), or null when it would not price
 * them: a formula input is cleared or malformed, or a required one is
 * missing or not positive.
 */
export const calculateProposedContractValue = (
  terms: Terms,
  responseType: NegotiationResponseType
): ProposedContractValue | null => {
  try {
    return responseType === 'msaas' ? calculateMsaas(terms) : calculateJob(terms);
  } catch (error) {
    if (error instanceof NotCalculable) return null;
    throw error;
  }
};

/** Signed proposed-minus-previous total, or null when either is unusable. */
export const getContractValueDifference = (
  proposedTotal: string | undefined,
  previousTotal: unknown
): string | null => {
  try {
    const proposed = readDecimal(proposedTotal);
    const previous = readDecimal(previousTotal);
    if (proposed === undefined || previous === undefined) return null;
    return toMoneyString(subtract(proposed, previous));
  } catch (error) {
    if (error instanceof NotCalculable) return null;
    throw error;
  }
};
